import { Fragment, useEffect, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { getCountryByIP } from '@/models/auth/ports'
import { breakpoints, colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { LocationHeaders } from '@/utils/cloudfront'
import { Label } from '@/components/text'

import usePopularCities from './use-popular-cities'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  color: ${colors.grey24};
  font-weight: 400;
  min-height: 16px;

  :first-of-type(div) {
    align-self: flex-start;
    flex-shrink: 0;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: initial;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
`

const List = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const CityLink = styled.a`
  color: ${colors.link};
  text-decoration: none;
  transition: opacity 0.2s ease-in-out;
  word-break: break-word;

  :hover {
    opacity: 0.8;
  }
`

const Separator = styled(Label)<{ fontSize: string }>`
  color: ${colors.grey48};
  font-weight: 600;
`

interface Location {
  state: string | null
  country_code_alpha2: string | null
}

const hasLocation = (location: Location | null) => {
  return location?.state && location?.country_code_alpha2
}

const PopularCities = ({
  locationHeaders
}: {
  locationHeaders: LocationHeaders
}) => {
  const { t } = useTranslation('home')
  const userLocationFromHeaders = {
    state: locationHeaders?.countryRegion,
    country_code_alpha2: locationHeaders?.country
  }
  const hasSSRLocation = hasLocation(userLocationFromHeaders)
  // Use the user location from CloudFront headers if available so
  // we can render the popular cities server-side
  const [userLocation, setUserLocation] = useState<Location | null>(
    userLocationFromHeaders
  )
  const [loading, setLoading] = useState(!hasSSRLocation)

  useEffect(() => {
    const getUserLocation = async () => {
      setLoading(true)
      try {
        const { body }: { body: Location } = await getCountryByIP()
        setUserLocation(body)
      } catch (error) {
        console.error('Error fetching user location:', error)
      } finally {
        setLoading(false)
      }
    }
    if (hasLocation(userLocation)) return
    getUserLocation()
  }, [])

  const stateCode = userLocation?.state?.toUpperCase() || 'default'
  const countryCode = userLocation?.country_code_alpha2 || 'CA'
  const cities = usePopularCities(stateCode, countryCode)

  if (loading || isEmptyOrNull(userLocation)) {
    return null
  }

  return (
    <Wrapper>
      <Label fontSize="14px" type="small">
        {t(`home:Popular cities`)}:
      </Label>
      <List>
        {cities.map((city, i) => {
          const isLast = i === cities.length - 1
          return (
            <Fragment key={city.slug}>
              <Link
                href={`/rental-listings/city/${city.slug}`}
                passHref
                legacyBehavior
              >
                <CityLink
                  id={`header-${stateCode.toLowerCase()}-city-${i + 1}`}
                >
                  <Label fontSize="14px" type="large">
                    {city.name}
                  </Label>
                </CityLink>
              </Link>
              {!isLast && <Separator fontSize="14px">·</Separator>}
            </Fragment>
          )
        })}
      </List>
    </Wrapper>
  )
}

export default PopularCities
